<template>
  <section
    class="DrawerModal"
    role="dialog"
    aria-modal="true"
    v-bind="{ id }"
  >
    <div class="DrawerModal__nav">
      <button
        class="DrawerModal__backButton"
        type="button"
        aria-label="Close"
        :aria-controls="id"
        @click="closeActiveModal()"
      >
        <Icon name="chevronLeft" />
      </button>

      <div class="DrawerModal__navTitleContainer">
        <Transition>
          <div
            v-if="label"
            class="DrawerModal__navTitle"
            aria-hidden="true"
          >
            {{ label }}
          </div>
        </Transition>
      </div>

      <slot name="nav">
        <div />
      </slot>
    </div>

    <div
      class="DrawerModal__content"
      data-theme="light"
    >
      <slot />
    </div>
  </section>
</template>

<script setup lang="ts">
import useModalStore from "@/stores/useModalStore";
import { useId } from "vue";
import Icon from "./Icon.vue";

defineProps<{
  label?: string,
}>();

const {
  closeActiveModal,
} = useModalStore();

const id = useId();
</script>

<style lang="scss" scoped>
.DrawerModal {
  --localColorSecondaryText: var(--themeLightTransparent700);
  --localColorSecondaryFill: var(--themeLightTransparent700);
  position: fixed;
  inset: 0;
  z-index: 999;

  display: grid;
  grid-template-rows: min-content auto;

  background: repeating-linear-gradient(to bottom, var(--themeColorPrimary), var(--themeColorPrimary) 50%, var(--themeColorNeutral0) 50%, var(--themeColorNeutral0) 100%);

  transition: 325ms transform;

  &.v-enter-from,
  &.v-leave-to {
    transform: translateX(calc(100% + 16px));
  }

  &:not(.v-enter-active, .v-leave-active):has(~ .DrawerModal:not(.v-leave-active)) {
    transform: translateX(-96px);
    transition-timing-function: var(--easeInOutCubic);
  }
}

.DrawerModal__nav {
  position: relative;

  display: grid;
  grid: auto / 44px 1fr 44px;
  align-items: center;
  height: calc(68px + var(--safeAreaInsetTop));
  padding-top: var(--safeAreaInsetTop);

  background-color: var(--themeColorPrimary);
  color: var(--localColorSecondaryText);
}

.DrawerModal__backButton {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;

  color: var(--localColorSecondaryFill);

}

.DrawerModal__navTitle {
  padding-top: var(--fontSafariOffset);

  color: var(--themeColorPrimaryForeground);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;

  transition: 250ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.DrawerModal__content {
  overflow-y: auto;
}
</style>
